/**
 * 24bottles — engrave coherence
 *
 * Products sold with engraving have two variants on the option "Style": variants[0] Classic and variants[1] Custom
 * (the engraved one, +6€). Rule enforced here, storefront side: the engraved variant is posted together with a
 * non-blank line item property "Engrave", and the classic variant never carries one. The engraving UI
 * (snippets/24-engrave-interaction.liquid) writes the text and switches the variant through an async re-render, so a
 * slow or failed request, a ?variant= link coming from a feed, or a blocked script used to produce orders with only
 * one half of the pair. Three layers, all fed by the ids that snippets/24-engrave-variants.liquid resolves:
 *
 * 1. Product forms: snippets/buy-buttons.liquid exposes data-engrave-classic-variant / data-engrave-custom-variant
 *    on the form. Right before any submission (submit, FormData construction, payment button click) the variant id
 *    is derived from the text: text → engraved variant, no text → classic variant and the blank property is dropped.
 * 2. Landing: a product page opened on the engraved variant without a text (the server already posts the classic
 *    one) is switched back to the classic variant, so price, gallery and form agree.
 * 3. Cart: snippets/line-item.liquid flags incoherent lines with [data-engrave-fix]. They are repaired here by adding
 *    the coherent variant and removing the wrong line; the server-side warning stays visible when this cannot run.
 */

const ENGRAVE_PROPERTY = 'properties[Engrave]';
const MAX_LENGTH = 10;
const PRODUCT_FORM_SELECTOR = 'form[is="product-form"]';

function root() {
  return window.Shopify?.routes?.root ?? '/';
}

function onReady(callback) {
  // setTimeout: the engraving UI restores ?engrave= in its own DOMContentLoaded handler, registered earlier.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(callback, 0), { once: true });
  } else {
    setTimeout(callback, 0);
  }
}

/* -------------------------------------------------------------------------- */
/* 1. Product forms                                                            */
/* -------------------------------------------------------------------------- */

function isProductForm(node) {
  return node instanceof HTMLFormElement && node.getAttribute('is') === 'product-form';
}

function engraveIds(form) {
  const classic = form.dataset.engraveClassicVariant;
  const custom = form.dataset.engraveCustomVariant;
  if (!classic || !custom) return null;
  return {
    classic,
    custom,
    classicOptionValue: form.dataset.engraveClassicOptionValue,
    customOptionValue: form.dataset.engraveCustomOptionValue,
  };
}

/** The confirmed engraving text of a form. Blank or whitespace only means "no engraving". */
function engraveText(form) {
  const input = form.elements.namedItem(ENGRAVE_PROPERTY);
  const value = String(input?.value ?? '').slice(0, MAX_LENGTH);
  return value.trim() ? value : '';
}

/**
 * Derive the variant from the text and write it back into the form. Returns the variant id the form now posts, or
 * null when the form is not an engrave form.
 */
function normalizeForm(form) {
  const ids = engraveIds(form);
  if (!ids) return null;
  const idInput = form.elements.namedItem('id');
  if (!idInput || !('value' in idInput)) return null;
  const text = engraveText(form);
  const target = text ? ids.custom : ids.classic;
  if (String(idInput.value) !== String(target)) idInput.value = target;
  const textInput = form.elements.namedItem(ENGRAVE_PROPERTY);
  if (textInput && 'value' in textInput && textInput.value !== text) textInput.value = text;
  return target;
}

// Capture phase on document: runs before the theme's own submit handler builds the request.
document.addEventListener('submit', (event) => {
  if (isProductForm(event.target)) normalizeForm(event.target);
}, true);

// Fired whenever a FormData is built from the form (theme ajax add-to-cart, native submission, third parties): the
// last word on what is actually sent.
document.addEventListener('formdata', (event) => {
  const form = event.target;
  if (!isProductForm(form)) return;
  const data = event.formData;
  const target = normalizeForm(form);
  if (target !== null) data.set('id', String(target));
  if (data.has(ENGRAVE_PROPERTY)) {
    const value = String(data.get(ENGRAVE_PROPERTY) ?? '').slice(0, MAX_LENGTH);
    if (value.trim()) data.set(ENGRAVE_PROPERTY, value);
    else data.delete(ENGRAVE_PROPERTY);
  }
}, true);

// Dynamic checkout buttons (Shop Pay, PayPal…) read the form themselves: normalize before their handler runs. Submit
// buttons are included for the sticky bar, whose buttons live outside the form (form= attribute).
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element
    ? event.target.closest('.shopify-payment-button, button[type="submit"], input[type="submit"]')
    : null;
  if (!trigger) return;
  const form = trigger.form ?? trigger.closest('form');
  if (isProductForm(form)) normalizeForm(form);
}, true);

// Any variant change (engraving UI, hidden picker, third parties) is re-checked against the text right away.
document.addEventListener('variant:change', (event) => {
  if (isProductForm(event.target)) normalizeForm(event.target);
});

/* -------------------------------------------------------------------------- */
/* 2. Landing on the engraved variant without a text                          */
/* -------------------------------------------------------------------------- */

function safeJson(text) {
  try {
    return JSON.parse(text || 'null');
  } catch (_) {
    return null;
  }
}

function normalizeLanding() {
  document.querySelectorAll(`${PRODUCT_FORM_SELECTOR}[data-engrave-custom-variant]`).forEach((form) => {
    const ids = engraveIds(form);
    if (!ids) return;
    normalizeForm(form);
    // A text restored from ?engrave= by the engraving UI switches the variant itself.
    if (engraveText(form)) return;
    // form.id is the <input name="id"> (named control), the attribute must be read explicitly.
    const picker = document.querySelector(`variant-picker[form-id="${form.getAttribute('id')}"]`);
    const shown = safeJson(picker?.querySelector('script[data-variant]')?.textContent);
    if (String(shown?.id ?? '') !== String(ids.custom)) return;
    if (typeof picker?.selectCombination !== 'function' || !ids.classicOptionValue) return;
    Promise.resolve(picker.selectCombination({ optionValues: [ids.classicOptionValue], productChange: false }))
      .catch(() => { /* the form already posts the classic variant, only the view stays behind */ });
  });
}

onReady(normalizeLanding);

/* -------------------------------------------------------------------------- */
/* 3. Cart safety net                                                          */
/* -------------------------------------------------------------------------- */

const ATTEMPTS_KEY = '24-engrave-coherence';
const MAX_ATTEMPTS = 2;
const inFlight = new Set();

function readAttempts() {
  try {
    return JSON.parse(sessionStorage.getItem(ATTEMPTS_KEY) || '{}') || {};
  } catch (_) {
    return {};
  }
}

function updateAttempt(lineKey, updater) {
  const all = readAttempts();
  const next = updater(all[lineKey] || { count: 0, added: false });
  if (next) all[lineKey] = next;
  else delete all[lineKey];
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(all));
  } catch (_) { /* storage unavailable: a duplicate add on retry is the only downside */ }
  return next;
}

async function postJson(path, body) {
  const response = await fetch(`${root()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(json.description || json.message || `${path} failed (${response.status})`);
  return json;
}

function coherentProperties(flag) {
  let properties = {};
  try {
    properties = JSON.parse(flag.dataset.properties || '{}') || {};
  } catch (_) {
    properties = {};
  }
  delete properties.Engrave;
  if (flag.dataset.engraveFix === 'orphan_text' && flag.dataset.engraveText) {
    properties.Engrave = flag.dataset.engraveText.slice(0, MAX_LENGTH);
  }
  return properties;
}

/**
 * Replace an incoherent line with its coherent counterpart: add first (if the add fails nothing has changed and the
 * server-side warning stays), then remove the wrong line. The "added" flag persisted per line key makes a retry
 * after a failed removal skip the add, so a line is never duplicated. At most MAX_ATTEMPTS per line key.
 */
async function repairLine(flag) {
  const lineKey = flag.dataset.lineKey;
  const fixVariantId = Number(flag.dataset.fixVariantId);
  const quantity = parseInt(flag.dataset.quantity, 10) || 1;
  if (!lineKey || !fixVariantId || inFlight.has(lineKey)) return;
  if ((readAttempts()[lineKey]?.count ?? 0) >= MAX_ATTEMPTS) return;

  let entry = updateAttempt(lineKey, (current) => ({ ...current, count: current.count + 1 }));
  inFlight.add(lineKey);
  flag.hidden = true;

  try {
    if (!entry.added) {
      await postJson('cart/add.js', { items: [{ id: fixVariantId, quantity, properties: coherentProperties(flag) }] });
      entry = updateAttempt(lineKey, (current) => ({ ...current, added: true }));
    }
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }));
    const cart = await postJson('cart/change.js', { id: lineKey, quantity: 0, sections });
    updateAttempt(lineKey, () => null);

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'line-item:change', cart },
    }));
  } catch (error) {
    console.warn('[24-engrave-coherence] cart repair failed', error);
    flag.hidden = false;
  } finally {
    inFlight.delete(lineKey);
  }
}

function scanCart() {
  document.querySelectorAll('[data-engrave-fix]').forEach((flag) => {
    if (!flag.hidden) repairLine(flag);
  });
}

let scanTimer = 0;
function scheduleScan() {
  if (scanTimer) return;
  scanTimer = window.setTimeout(() => {
    scanTimer = 0;
    scanCart();
  }, 60);
}

// The cart drawer is server-rendered on every page and re-rendered on each cart change, so a flagged line shows up
// as a DOM mutation wherever it happens (add to cart on the PDP, drawer opening, cart page, bfcache restore).
onReady(() => {
  scheduleScan();
  new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });
});
